/*
 * Copyright (c) 2023 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Saturday, March 25th 2023, 12:15:48 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import CodeIcon from "@mui/icons-material/Code";
import { ContextTagSelectorButtonWrapper } from "./ContextTagSelectorButtonWrapper";
import { TextFormInput, TextFormInputProps } from "ui-widgets";
import { useOSFormContext } from "ui-widgets/forms/providers";
import { ContextTagSelectorProps } from "./ContextTagSelector";

type TextFormInputWithTagSelectorProps<TData> = TextFormInputProps<TData> & {
  contextTagsAPIFilters?: ContextTagSelectorProps["contextTagsAPIFilters"];
};

const withTagSelector = <TData,>(TextFieldComponent: typeof TextFormInput) => {
  return ({ contextTagsAPIFilters, ...props }: TextFormInputWithTagSelectorProps<TData>) => {
    const { setValue, getValues, watch } = useOSFormContext<TData>();

    const fieldKey = props.schema_field_key as any;

    const previousValue = getValues(fieldKey);

    return (
      <TextFieldComponent
        {...props}
        InputLabelProps={{ shrink: Boolean(watch(fieldKey)) }}
        InputProps={{
          endAdornment: (
            <ContextTagSelectorButtonWrapper
              onCopyContextTag={({ tag }) => setValue(fieldKey, `${previousValue}${tag}` as any, { shouldDirty: true })}
              copiedMessage="Default value updated"
              contextTagsAPIFilters={contextTagsAPIFilters}
            >
              {({ openContextTagsFn }) => (
                <Tooltip title={<Typography variant="body1">Use Context Tags</Typography>} placement="left-start" arrow>
                  <IconButton color="primary" onClick={openContextTagsFn}>
                    <CodeIcon />
                  </IconButton>
                </Tooltip>
              )}
            </ContextTagSelectorButtonWrapper>
          ),
        }}
      />
    );
  };
};

export const TextFormInputWithTagSelector = withTagSelector(TextFormInput) as <TData>(
  props: TextFormInputWithTagSelectorProps<TData>
) => JSX.Element;
